'use strict';
import Mustache from 'mustache';
import './ecom-styles.scss';

const template = `
  <div class="ecom-popup__content">
    {{#purchaseLinks}}
      <a href="{{purchaseUrl}}" class="ecom-popup__link" target="_blank" rel="noopener">
        <img src="{{logo}}" alt="{{name}}" class="ecom-popup__logo"/>
        <span class="ecom-popup__stock {{#stockStatusCode}}ecom-popup__stock--{{stockStatusCode}}{{/stockStatusCode}}">{{#stockStatus}}<span class="icon icon--checkmark"></span>{{stockStatus}}{{/stockStatus}}</span>
        <span class="ecom-popup__price">{{#price}}{{price}}{{currency}}{{/price}}</span>
        <span class="icon icon--arrow-right"></span>
      </a>
    {{/purchaseLinks}}
  </div>
`

class ECom {

  lang: string = '';
  buttons: { [key: string]: HTMLButtonElement[] } = {};
  purchaseData: { [key: string]: any } = {};
  popupEl: HTMLDivElement | null = null;

  constructor() {
    this.lang = document.documentElement.getAttribute('stage-lang') || '';
    console.log('eCom: enabled');
  }

  /**
   * Initialize eCom buttons
   * Fetches purchase links for all buttons. Backend handles area restrictions and only returns active dealers for user.
   * @param productCodeButtons NodeList of buttons with data-ecom-product-code attribute
   * @return void
   */
  async initEcomButtons(productCodeButtons: NodeListOf<Element>) {
    // for test
    if (productCodeButtons.length) {
      const productCodes: string[] = [];
      productCodeButtons.forEach((btn) => {
        const code = btn.getAttribute('data-ecom-product-code');
        if (code && productCodes.indexOf(code) === -1) {
          productCodes.push(code);

          if (!this.buttons[code]) this.buttons[code] = [];
          this.buttons[code].push(btn as HTMLButtonElement);
        }
      });

      if (productCodes.length) {
        let ecomPurchaseLinks = await fetch(
          `/api/v1/dealers/ecom/?stage-language=${this.lang}&productCodes=${productCodes.join(',')}`,
          { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(response => response.json())
          .then(data => {
            return data.result.ecomPurchaseLinks;

          }).catch(_ => {
            console.log('eCom: error fetching.');
          });

        if (ecomPurchaseLinks) {
          this.purchaseData = ecomPurchaseLinks;

          Object.keys(ecomPurchaseLinks).forEach((code) => {
            if (this.buttons[code]) {
              this.buttons[code].forEach(button => {
                button.style.visibility = 'visible';

                button.addEventListener('click', (e) => {
                  this.openEcomPopup(this.purchaseData[code], button);
                });
              })
            }
          });

          document.addEventListener('click', this.onOutsideClick.bind(this));
          document.addEventListener('keydown', this.onDocumentKeyDown.bind(this));
          const resizeListener = this.debounce(() => {
            this.closeEcomPopup(false);
          }, 200);

          window.addEventListener('resize', resizeListener.bind(this));

        }
      }
    }
  }

  debounce(func, wait) {
    const self = this;
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func.apply(self, args);
      }, wait);
    };
  }

  onOutsideClick(event: Event): void {
    if (event.target instanceof HTMLElement && Object.values(this.buttons).flat().every(btn => !(btn as Node).contains(event.target as Node)) && (this.popupEl && !this.popupEl.contains(event.target as Node))) {
      this.closeEcomPopup(true);
    }
  }

  /**
  * Check for escape to close menu
  */
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    this.closeEcomPopup(true);
  }

  closeEcomPopup(focusButton: boolean = true) {
    if (this.popupEl) {
      this.popupEl.remove();
      this.popupEl = null;
    }

    Object.values(this.buttons).forEach(code => {
      code.forEach((btn) => {
        if (btn.getAttribute('aria-expanded') === 'true') {
          btn.setAttribute('aria-expanded', 'false');
          if (focusButton) btn.focus();
        }
      });
    });
  }

  /*
  * Open eCom popup
  * @param data Purchase links data
  * @param button Button element
  */
  openEcomPopup(data: any, button: HTMLButtonElement) {
    if (this.popupEl) {
      this.closeEcomPopup(false);
    }
    if (!data || !button) return;
    const popupEl = document.createElement('div');
    popupEl.className = 'ecom-popup hidden';

    button.after(popupEl);

    this.popupEl = popupEl;

    const buttonProperties = button.getBoundingClientRect();
    const position = button.dataset.ecomPosition || 'below';
    this.popupEl.innerHTML = Mustache.render(template, { purchaseLinks: data });

    this.popupEl.classList.remove('hidden');
    this.popupEl.classList.add(`ecom-popup--${position}`);

    this.popupEl.style.top = position === 'below' ? button.offsetTop + button.offsetHeight + 6 + 'px' : button.offsetTop - buttonProperties.height - 12 + 'px';
    this.popupEl.style.left = (button.offsetLeft - (this.popupEl.getBoundingClientRect().width - buttonProperties.width) / 2) + 'px';
    this.popupEl.style.opacity = '1';

    this.popupEl.querySelectorAll('.ecom-popup__link').forEach((link) => {

      const familyEl = link.closest('[data-ecom-family-code]');
      const itemEl = link.closest('[data-ecom-item-code]');

      /**
       * Send ecom dataLayer if we have family and item data
       * requires following attribute pairs to be present on ancestor elements:
       * data-ecom-family-name & data-ecom-family-code
       * data-ecom-item-size & data-ecom-item-code
      */
      if (familyEl && itemEl) {
        const ecomProperties = {
          productFamily: familyEl.getAttribute('data-ecom-family-name') || '',
          productFamilyCode: familyEl.getAttribute('data-ecom-family-code') || '',
          tyreSize: itemEl.getAttribute('data-ecom-item-size') || '',
          productCode: itemEl.getAttribute('data-ecom-item-code') || ''
        }

        link.addEventListener('click', (e) => {
          e.preventDefault();
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            'event': 'ecom_exit',
            ...ecomProperties
          });

          const anchor = (e.target as HTMLLinkElement).closest("a");
          if (!anchor) return;
          window.open(anchor.getAttribute('href') || '', '_blank');
        });
      }
    });

    button.setAttribute('aria-expanded', 'true');
  }
};

interface ECom {
  position: string;
  openEcomPopup: (data: any, button: HTMLButtonElement) => void;
  onOutsideClick: (event: Event) => void;
  onDocumentKeyDown: (event: KeyboardEvent) => void;
  closeEcomPopup: (focusButton?: boolean) => void;
}

export function initEcomButtons(buttons: NodeListOf<HTMLButtonElement>) {
  new ECom().initEcomButtons(buttons);
}

export default ECom;
